import { createAsyncThunk } from '@reduxjs/toolkit'

import { AccountsApi } from '../../Accounts/api/accountsApi'
import { GetTopAccountsRequest, SetExcludedFromTopRequest } from '../../Accounts/api/types'
import { AdminDashboardService } from '../api/adminDashboardApi'
import {
  GetAdminBreachEventsRequest,
  GetAdminDashboardMetricsByCountryRequest,
  GetAdminDashboardMetricsByCountryChartRequest,
  GetAdminDashboardMetricsRequest,
  GetAdminRegistrationPlatformMetricsRequest,
  GetAdminBrokerServersRequest,
  GetTradingLogStatsRequest,
  GetPlatformRequestStatsRequest,
  GetQuestionnaireStatsRequest,
  GetTrackedLinkClickCountryStatsRequest,
  GetTrackedLinkClickStatsRequest,
} from '../api/types'

// ---------------------------------------------------------------------

export const SLICE_NAME = 'adminDashboard'

export const GetAdminDashboardMetrics = createAsyncThunk(
  `${SLICE_NAME}/getMetrics`,
  async (data: GetAdminDashboardMetricsRequest) => {
    try {
      const res = await AdminDashboardService.getMetrics(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetAdminBreachEvents = createAsyncThunk(
  `${SLICE_NAME}/getBreachEvents`,
  async (data?: GetAdminBreachEventsRequest) => {
    try {
      const res = await AdminDashboardService.getBreachEvents(data)
      return res.data.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetAdminRegistrationPlatformMetrics = createAsyncThunk(
  `${SLICE_NAME}/getRegistrationPlatformMetrics`,
  async (data: GetAdminRegistrationPlatformMetricsRequest) => {
    try {
      const res = await AdminDashboardService.getRegistrationPlatformMetrics(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetAdminDashboardMetricsByCountry = createAsyncThunk(
  `${SLICE_NAME}/getMetricsByCountry`,
  async (data: GetAdminDashboardMetricsByCountryRequest) => {
    try {
      const res = await AdminDashboardService.getMetricsByCountry(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetAdminDashboardMetricsByCountryChart = createAsyncThunk(
  `${SLICE_NAME}/getMetricsByCountryChart`,
  async (data: GetAdminDashboardMetricsByCountryChartRequest) => {
    try {
      const res = await AdminDashboardService.getMetricsByCountryChart(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetAdminBrokerServers = createAsyncThunk(
  `${SLICE_NAME}/getBrokerServers`,
  async (data: GetAdminBrokerServersRequest) => {
    try {
      const res = await AdminDashboardService.getBrokerServers(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetAdminDashboardTopAccounts = createAsyncThunk(
  `${SLICE_NAME}/getTopAccounts`,
  async (data: GetTopAccountsRequest) => {
    try {
      const res = await AccountsApi.getTopAccounts(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const SetExcludedFromTop = createAsyncThunk(
  `${SLICE_NAME}/setExcludedFromTop`,
  async (data: SetExcludedFromTopRequest) => {
    try {
      const res = await AccountsApi.setExcludedFromTop(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const SetExcludedFromTopAndRefresh = createAsyncThunk(
  `${SLICE_NAME}/setExcludedFromTopAndRefresh`,
  async (
    { accountId, excludedFromTop, ...rest }: GetTopAccountsRequest & SetExcludedFromTopRequest,
    { dispatch, rejectWithValue },
  ) => {
    try {
      await AccountsApi.setExcludedFromTop({ accountId, excludedFromTop })

      const topAccounts = await dispatch(GetAdminDashboardTopAccounts(rest)).unwrap()

      return topAccounts
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const GetTradingLogStats = createAsyncThunk(
  `${SLICE_NAME}/getTradingLogStats`,
  async (data?: GetTradingLogStatsRequest) => {
    try {
      const res = await AdminDashboardService.getTradingLogStats(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetUserQuestionnaireStats = createAsyncThunk(
  `${SLICE_NAME}/getUserQuestionnaireStats`,
  async (data?: GetQuestionnaireStatsRequest) => {
    try {
      const res = await AdminDashboardService.getUserQuestionnaireStats(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetPlatformRequestStats = createAsyncThunk(
  `${SLICE_NAME}/getPlatformRequestStats`,
  async (data?: GetPlatformRequestStatsRequest) => {
    try {
      const res = await AdminDashboardService.getPlatformRequestStats(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetTrackedLinkClickStats = createAsyncThunk(
  `${SLICE_NAME}/getTrackedLinkClickStats`,
  async (data?: GetTrackedLinkClickStatsRequest) => {
    try {
      const res = await AdminDashboardService.getTrackedLinkClickStats(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)

export const GetTrackedLinkClickCountryStats = createAsyncThunk(
  `${SLICE_NAME}/getTrackedLinkClickCountryStats`,
  async (data?: GetTrackedLinkClickCountryStatsRequest) => {
    try {
      const res = await AdminDashboardService.getTrackedLinkClickCountryStats(data)
      return res.data
    } catch (error) {
      throw new Error(error.message)
    }
  },
)
